/**
 * Step 7 — Approval Summary + Proposed Appointments
 * Reads from /api/rendering-provider/{pa_id}/approval-summary and
 * /api/rendering-provider/{pa_id}/propose-appointments
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2, Calendar, Clock, AlertTriangle, MapPin, Stethoscope,
  ShieldCheck, FileText, Loader2, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Step7ApprovalSummary({ t, isDark, paId, autoLoadSlots = false }) {
  const [summary, setSummary] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proposing, setProposing] = useState(false);

  const proposeSlots = async () => {
    setProposing(true);
    try {
      const r = await axios.post(`${API}/rendering-provider/${paId}/propose-appointments`, {}, { withCredentials: true });
      setSlots(r.data.slots || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate appointment slots");
    } finally {
      setProposing(false);
    }
  };

  useEffect(() => {
    if (!paId) return;
    axios.get(`${API}/rendering-provider/${paId}/approval-summary`, { withCredentials: true })
      .then(async r => {
        setSummary(r.data);
        // Auto-load proposed appointments for approved PAs so admins can review
        // the predicted slots without an extra click.
        if (autoLoadSlots) {
          try {
            const slotRes = await axios.post(`${API}/rendering-provider/${paId}/propose-appointments`, {}, { withCredentials: true });
            setSlots(slotRes.data.slots || []);
          } catch { /* non-fatal — user can still click Generate Slots */ }
        }
      })
      .catch(e => toast.error(e.response?.data?.detail || "Failed to load summary"))
      .finally(() => setLoading(false));
  }, [paId, autoLoadSlots]);

  const selectSlot = async (slot) => {
    if (!slot.within_pa_validity || !slot.within_eligibility) {
      toast.error("This slot falls outside the valid scheduling window");
      return;
    }
    try {
      const r = await axios.post(`${API}/rendering-provider/${paId}/select-appointment`, { slot_id: slot.slot_id }, { withCredentials: true });
      if (r.data.ok) {
        setSelectedSlot(slot);
        toast.success("Appointment scheduled");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to select slot");
    }
  };

  if (loading) return <div className="py-16 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>;
  if (!summary) return <div className={`p-6 ${t.textMuted}`}>No approval data available</div>;

  const a = summary.approval;
  const surfaceCard = `${t.card} rounded-lg border shadow-sm`;

  return (
    <div className="space-y-6" data-testid="step-7-approval-summary">
      {/* Hero */}
      <div className={`${surfaceCard} p-5 flex items-center gap-4 ${isDark ? "bg-gradient-to-r from-emerald-500/10 to-slate-900" : "bg-gradient-to-r from-emerald-50 to-blue-50"}`}>
        <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isDark ? "bg-emerald-500/20" : "bg-emerald-100"}`}>
          <CheckCircle2 className={`w-8 h-8 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
        </div>
        <div>
          <div className={`text-xs font-semibold uppercase ${t.textMuted}`}>Authorization Approved</div>
          <div className={`text-xl font-bold ${t.textStrong}`}>{a.auth_number}</div>
          <div className={`text-xs ${t.textMuted}`}>
            Effective {a.effective_date} → expires {a.expiration_date} · {a.payer_name}
          </div>
        </div>
        <Badge className="ml-auto bg-emerald-500 text-white">{a.status?.toUpperCase()}</Badge>
      </div>

      {/* Section 1 — Approval Details */}
      <div className={surfaceCard}>
        <div className={`px-5 py-3 border-b ${t.cardHead}`}>
          <h3 className={`text-sm font-bold ${t.cardHeadText} uppercase flex items-center gap-2`}>
            <FileText className="w-4 h-4" /> Section 1 — Approval Details
          </h3>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field t={t} label="Authorization #" value={a.auth_number} />
          <Field t={t} label="Status" value={a.status} />
          <Field t={t} label="Payer" value={a.payer_name} />
          <Field t={t} label="Plan" value={a.plan_name} />
          <Field t={t} label="Effective Date" value={a.effective_date} />
          <Field t={t} label="Expiration Date" value={a.expiration_date} />
          <Field t={t} label="Approved Visits/Units" value={a.approved_visits} />
          <Field t={t} label="Place of Service" value={a.place_of_service} />
          <Field t={t} label="Submission Channel" value={a.submission_channel} />
          <Field t={t} label="Approval Source" value={a.approval_source} />
          <Field t={t} label="Approval Timestamp" value={a.approval_timestamp && new Date(a.approval_timestamp).toLocaleString()} />
          <Field t={t} label="Eligibility End Date" value={summary.eligibility_end_date || "—"} />
          {a.payer_notes && (
            <div className="md:col-span-2 lg:col-span-3">
              <div className={`text-[11px] font-semibold uppercase ${t.textMuted} mb-1`}>Payer Notes</div>
              <div className={`text-sm ${t.text} p-3 rounded ${isDark ? "bg-slate-800/40" : "bg-gray-50"}`}>{a.payer_notes}</div>
            </div>
          )}
          {a.rendering_provider && (
            <div className="md:col-span-2 lg:col-span-3">
              <div className={`text-[11px] font-semibold uppercase ${t.textMuted} mb-1`}>Approved Rendering Provider</div>
              <div className={`text-sm ${t.text} p-3 rounded flex items-center gap-3 ${isDark ? "bg-slate-800/40" : "bg-gray-50"}`}>
                <Stethoscope className="w-5 h-5" />
                <div>
                  <div className="font-semibold">{a.rendering_provider.name}</div>
                  <div className="text-xs">NPI {a.rendering_provider.npi}{a.rendering_provider.specialty ? ` · ${a.rendering_provider.specialty}` : ""}</div>
                  {(a.rendering_provider.address || a.rendering_provider.city) && (
                    <div className="text-xs flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" />
                      {[a.rendering_provider.address, a.rendering_provider.city, a.rendering_provider.state, a.rendering_provider.zip].filter(Boolean).join(", ")}
                    </div>
                  )}
                </div>
                {a.rendering_provider.in_network && <Badge className="ml-auto bg-emerald-500 text-white"><ShieldCheck className="w-3 h-3 mr-1" />In-Network</Badge>}
              </div>
            </div>
          )}
          <div className="md:col-span-2 lg:col-span-3">
            <div className={`text-[11px] font-semibold uppercase ${t.textMuted} mb-1`}>Approved Codes</div>
            <div className="flex flex-wrap gap-2">
              {(a.approved_dx || []).map((d, i) => (
                <Badge key={"dx" + i} variant="outline" className="font-mono text-xs">{d.code} · {d.description}</Badge>
              ))}
              {(a.approved_cpts || []).map((p, i) => (
                <Badge key={"cpt" + i} className="bg-blue-500 text-white font-mono text-xs">{p.code} · {p.description}</Badge>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Section 2 — Proposed Appointments */}
      <div className={surfaceCard}>
        <div className={`px-5 py-3 border-b ${t.cardHead} flex items-center justify-between`}>
          <h3 className={`text-sm font-bold ${t.cardHeadText} uppercase flex items-center gap-2`}>
            <Calendar className="w-4 h-4" /> Section 2 — Proposed Appointments
          </h3>
          <Button size="sm" onClick={proposeSlots} disabled={proposing} className={t.btn} data-testid="propose-slots-btn">
            {proposing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Clock className="w-4 h-4 mr-1" />}
            {slots.length > 0 ? "Refresh Predictions" : "Generate Slots"}
          </Button>
        </div>
        <div className="p-5">
          <div className={`mb-4 p-3 rounded text-xs flex items-start gap-2 ${isDark ? "bg-amber-500/10 text-amber-300" : "bg-amber-50 text-amber-800"}`}>
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              Scheduling window ends on <span className="font-semibold">{summary.scheduling_window_end}</span> — the earlier of <span className="font-semibold">PA expiration ({a.expiration_date})</span> or <span className="font-semibold">insurance eligibility end ({summary.eligibility_end_date || "n/a"})</span>. Slots outside this window are not shown.
            </div>
          </div>
          {slots.length === 0 && !proposing && (
            <div className={`p-10 text-center ${t.textMuted}`}>
              Click <span className="font-semibold">Generate Slots</span> to propose appointments within the valid window.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {slots.map((s, i) => {
              const isSelected = selectedSlot?.slot_id === s.slot_id;
              const inWindow = s.within_pa_validity && s.within_eligibility;
              return (
                <div key={s.slot_id} className={`p-4 rounded-lg border-2 transition-all ${
                  isSelected
                    ? (isDark ? "border-emerald-500 bg-emerald-500/10" : "border-emerald-500 bg-emerald-50")
                    : (inWindow
                      ? `${t.border} ${isDark ? "hover:bg-slate-800" : "hover:bg-blue-50"}`
                      : "border-red-300 opacity-60")
                }`} data-testid={`slot-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-bold ${t.textStrong}`}>{s.date}</span>
                    <Badge variant="outline" className="font-mono text-xs">{s.time}</Badge>
                  </div>
                  <div className={`text-xs ${t.textMuted} mb-1`}>{s.appointment_type}</div>
                  <div className={`text-xs ${t.text}`}><Stethoscope className="w-3 h-3 inline mr-1" />{s.rendering_provider}</div>
                  <div className="flex items-center gap-1 mt-2">
                    {s.within_pa_validity ? <Badge className="bg-emerald-500 text-white text-[10px]">PA Valid</Badge> : <Badge className="bg-red-500 text-white text-[10px]">PA Expired</Badge>}
                    {s.within_eligibility ? <Badge className="bg-emerald-500 text-white text-[10px]">Elig OK</Badge> : <Badge className="bg-red-500 text-white text-[10px]">Elig End</Badge>}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => selectSlot(s)}
                    disabled={!inWindow || isSelected}
                    variant={isSelected ? "default" : "outline"}
                    className={`w-full mt-3 ${isSelected ? t.btn : t.btnOutline}`}
                    data-testid={`select-slot-${i}`}
                  >
                    {isSelected ? <><Check className="w-3 h-3 mr-1" />Selected</> : "Select Slot"}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ t, label, value }) {
  return (
    <div>
      <div className={`text-[11px] font-semibold uppercase ${t.textMuted}`}>{label}</div>
      <div className={`text-sm ${t.text}`}>{value || "—"}</div>
    </div>
  );
}
